import { useCallback, useMemo } from 'react';

import { Webpage } from '@/types';
import { StorageKey, useStorage } from '@/utils/storage/useStorage.util';

export interface WebpageSort {
  // A real `keyof Webpage` forwarded to the server query. `'riskScore'` maps to
  // the backend `risk_score` inside useWebpages (ENG-4395 — server-side risk sort).
  orderBy: keyof Webpage;
  descending: boolean;
}

/**
 * Normalize a persisted sort read from localStorage. Pre-ENG-4395 builds
 * persisted the synthetic client-only key `'risk'`; it maps losslessly to the
 * real `'riskScore'` field so a user's chosen risk sort survives the upgrade
 * (now server-side). Read-time only — the next setSort persists the new key.
 */
export function normalizeWebpageSort(sort: WebpageSort): WebpageSort {
  if ((sort.orderBy as string) === 'risk') {
    return { orderBy: 'riskScore', descending: sort.descending };
  }
  return sort;
}

export interface WebpageFilters {
  type: string[];
  service: string[];
  viewed: string[];
}

export const DEFAULT_SORT: WebpageSort = {
  orderBy: 'visited',
  descending: true,
};

export function useWebpageFilters() {
  const [viewedKeys, setViewedKeys] = useStorage<string[]>(
    { key: StorageKey.WEBPAGE_VIEWED },
    []
  );

  const viewedSet = useMemo(() => new Set(viewedKeys ?? []), [viewedKeys]);

  const markViewed = useCallback(
    (key: string) => {
      setViewedKeys(prev => {
        const current = prev ?? [];
        if (current.includes(key)) return current;
        return [...current, key];
      });
    },
    [setViewedKeys]
  );

  const isViewed = useCallback(
    (key: string) => viewedSet.has(key),
    [viewedSet]
  );

  const [rawSort, setSort] = useStorage<WebpageSort>(
    { key: 'webpage.sort' },
    DEFAULT_SORT
  );
  const sort = useMemo(() => normalizeWebpageSort(rawSort), [rawSort]);

  const [filters, setFilters] = useStorage<WebpageFilters>(
    { key: 'webpage.filters' },
    { type: [], service: [], viewed: [] }
  );

  const filterByViewed = useCallback(
    (webpages: Webpage[]) => {
      if (filters.viewed.length !== 1) return webpages;
      if (filters.viewed[0] === 'viewed') {
        return webpages.filter(wp => viewedSet.has(wp.key));
      }
      if (filters.viewed[0] === 'not-viewed') {
        return webpages.filter(wp => !viewedSet.has(wp.key));
      }
      return webpages;
    },
    [filters.viewed, viewedSet]
  );

  const isDefaultSort =
    sort.orderBy === DEFAULT_SORT.orderBy &&
    sort.descending === DEFAULT_SORT.descending;

  const hasActiveFilters =
    filters.type.length > 0 ||
    filters.service.length > 0 ||
    filters.viewed.length > 0 ||
    !isDefaultSort;

  const clearFilters = useCallback(() => {
    setFilters({ type: [], service: [], viewed: [] });
    setSort(DEFAULT_SORT);
  }, [setFilters, setSort]);

  return {
    sort,
    setSort,
    filters,
    setFilters,
    filterByViewed,
    hasActiveFilters,
    clearFilters,
    isDefaultSort,
    markViewed,
    isViewed,
    viewedSet,
  };
}
